// Top bar component - minimal navigation for sub-views

import { css, html } from 'lit';
import { authState, isStaff } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { debugState } from '../../state/debug-state.js';
import { popoverState } from '../../state/popover-state.js';
import './friends-widget.js';
import './notifications-widget.js';

const POPOVER_ID = 'user-menu';

/**
 * Thin dark top bar with logo and user avatar
 * Logo navigates to title screen, avatar navigates to account
 */
class TopBar extends BaseComponent {
  static properties = {
    user: { type: Object },
    _showDebug: { type: Boolean, state: true },
    _jwCopied: { type: Boolean, state: true },
    _windowWidth: { type: Number, state: true },
    _windowHeight: { type: Number, state: true },
    _menuOpen: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .top-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;
      height: 52px;
      padding: 0 1rem;
      background-color: #0a0a1a;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      flex-shrink: 0;
    }

    .logo {
      font-family: 'MedievalSharp', Georgia, serif;
      font-size: 1.25rem;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.6);
      text-decoration: none;
      letter-spacing: 0.1em;
    }

    .user-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid rgba(255, 255, 255, 0.15);
      transition: border-color 0.2s ease;
    }

    .user-avatar-placeholder {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.7);
      border: 2px solid rgba(255, 255, 255, 0.15);
      transition: border-color 0.2s ease;
    }

    .user-section {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .user-menu {
      position: relative;
    }

    .user-trigger {
      background: transparent;
      border: none;
      padding: 2px 4px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.85rem;
      font-weight: 500;
      transition: color 0.15s;
    }

    .user-trigger:hover,
    .user-trigger:focus {
      color: rgba(255, 255, 255, 1);
      outline: none;
    }

    .user-name {
      max-width: 12ch;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .caret {
      font-size: 0.7rem;
      opacity: 0.7;
    }

    .user-menu-panel {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 4px;
      min-width: 180px;
      background: #1a1a2a;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      z-index: 1500;
      padding: 4px 0;
    }

    .user-menu-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      background: transparent;
      border: none;
      padding: 8px 14px;
      color: rgba(255, 255, 255, 0.85);
      font-size: 0.85rem;
      text-align: left;
      cursor: pointer;
    }

    .user-menu-item:hover,
    .user-menu-item:focus {
      background: rgba(255, 255, 255, 0.06);
      outline: none;
    }

    .user-menu-divider {
      height: 1px;
      margin: 4px 0;
      background: rgba(255, 255, 255, 0.08);
    }

    .debug-info {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      color: rgba(255, 255, 255, 0.5);
      font-size: 0.75rem;
      font-family: monospace;
      letter-spacing: 0.05em;
    }
  `;

  constructor() {
    super();
    /** @type {import('../../types/jsdoc-types.js').User|null} */
    this.user = null;
    this._showDebug = debugState.enabled;
    this._windowWidth = window.innerWidth;
    this._windowHeight = window.innerHeight;
    this._menuOpen = false;
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleResize = this._handleResize.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    /** @type {(() => void) | null} */
    this._unsubscribeDebug = null;
    /** @type {(() => void) | null} */
    this._unsubscribePopover = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    window.addEventListener('keydown', this._handleKeyDown);
    window.addEventListener('resize', this._handleResize);
    document.addEventListener('click', this._handleDocumentClick);
    // Re-render whenever debug state flips (in case another component
    // somewhere else toggles it).
    this._unsubscribeDebug = debugState.subscribe(() => {
      this._showDebug = debugState.enabled;
    });
    this._unsubscribePopover = popoverState.subscribe((openId) => {
      this._menuOpen = openId === POPOVER_ID;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this._handleKeyDown);
    window.removeEventListener('resize', this._handleResize);
    document.removeEventListener('click', this._handleDocumentClick);
    this._unsubscribeDebug?.();
    this._unsubscribeDebug = null;
    this._unsubscribePopover?.();
    this._unsubscribePopover = null;
  }

  /** @param {MouseEvent} e */
  _handleDocumentClick(e) {
    if (!this._menuOpen) return;
    const path = e.composedPath();
    if (!path.includes(this)) popoverState.close(POPOVER_ID);
  }

  /** @param {Event} e */
  _toggleMenu(e) {
    e.stopPropagation();
    popoverState.toggle(POPOVER_ID);
  }

  /** @param {KeyboardEvent} e */
  _handleKeyDown(e) {
    if (e.key === '`' && isStaff()) {
      debugState.toggle();
    }
  }

  _handleResize() {
    this._windowWidth = window.innerWidth;
    this._windowHeight = window.innerHeight;
  }

  get _breakpoint() {
    const w = this._windowWidth;
    if (w >= 1400) return 'xxl';
    if (w >= 1200) return 'xl';
    if (w >= 992) return 'lg';
    if (w >= 768) return 'md';
    if (w >= 576) return 'sm';
    return 'xs';
  }

  get _userInitial() {
    const name = this.user?.username || this.user?.email || 'U';
    return name.charAt(0).toUpperCase();
  }

  async _copyJWT() {
    const token = await authState.getAccessToken();
    if (token) {
      await navigator.clipboard.writeText(token);
      this._jwCopied = true;
      setTimeout(() => {
        this._jwCopied = false;
      }, 2000);
    }
  }

  _handleSignOut() {
    popoverState.close(POPOVER_ID);
    this.dispatchEvent(new CustomEvent('logout', { bubbles: true }));
  }

  render() {
    return html`
      <div class="top-bar">
        <span class="logo">RPGish</span>
        ${
          this._showDebug
            ? html` <span class="debug-info">${this._windowWidth} × ${this._windowHeight} · ${this._breakpoint}</span> `
            : ''
        }
        ${
          this.user
            ? html`
              <div class="user-section">
                <friends-widget></friends-widget>
                <notifications-widget></notifications-widget>
                <div class="user-menu" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
                  <button class="user-trigger" @click="${this._toggleMenu}" aria-haspopup="menu" aria-expanded="${this._menuOpen}">
                    ${
                      this.user.picture
                        ? html`<img class="user-avatar" src="${this.user.picture}" alt="${this.user.username || ''}" />`
                        : html`<div class="user-avatar-placeholder">${this._userInitial}</div>`
                    }
                    <span class="user-name">${this.user.username || this.user.email || ''}</span>
                    <i class="bi bi-caret-down-fill caret"></i>
                  </button>
                  ${
                    this._menuOpen
                      ? html`
                        <div class="user-menu-panel" role="menu">
                          ${
                            this._showDebug
                              ? html`
                                <button class="user-menu-item" role="menuitem" @click="${this._copyJWT}">
                                  <i class="bi bi-clipboard"></i>
                                  ${this._jwCopied ? 'Copied!' : 'Copy JWT'}
                                </button>
                                <div class="user-menu-divider"></div>
                              `
                              : ''
                          }
                          <button class="user-menu-item" role="menuitem" @click="${this._handleSignOut}">
                            <i class="bi bi-box-arrow-right"></i>
                            Sign Out
                          </button>
                        </div>
                      `
                      : ''
                  }
                </div>
              </div>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('top-bar', TopBar);
